/**
 * Regras puras do alerta "não é possível conectar ao servidor" (R2CLOUD-1169), fora do componente
 * Angular para poderem ser testadas sem TestBed — mesmo arranjo de `stomp-worker-watchdog.ts`,
 * `logout-completion.ts` e `http-unauthorized-rules.ts`.
 *
 * O defeito medido em DEV, 31/07/2026: o `client-status-toolbar.component.ts` abria um diálogo
 * BLOQUEANTE (`disableClose: true`) com "Não é possível conectar ao servidor" depois de 15 s de
 * `STOMP_NOT_CONNECTED` — e **15 s é menos que o tempo que o próprio portal leva para subir a
 * sessão STOMP numa carga limpa**. O orçamento medido de recuperação foi de **21 a 26 s**: o
 * primeiro Web Worker do STOMP nasce mudo, o watchdog do R2CLOUD-1165 o descarta aos 10 s e o
 * worker seguinte leva mais ~6,7 s até o `CONNECTED`. Ou seja, o modal não relatava falha nenhuma:
 * ele aparecia em TODA carga limpa, por cima da tela, enquanto o portal ainda estava conectando.
 * Foi assim que o ticket registrou "3 de 3 tentativas de abrir Criar usuário e Editar responderam
 * 'Não é possível conectar ao servidor'" — a operação não falhou, o modal é que estava na frente.
 *
 * Piora o quadro o desfecho oferecido: o botão "Tentar novamente" faz `window.location.reload()`,
 * que REINICIA a corrida do zero (novo boot da SPA, novo worker, novo watchdog), e depois de três
 * recargas o portal passa a oferecer "Modo contingência" — degradação de produto causada por um
 * prazo mal calibrado, não por servidor fora.
 *
 * A regra aqui é defesa em profundidade: **o portal não acusa queda de servidor enquanto ainda está
 * dentro do tempo em que ele mesmo sabe que pode conectar.** A correção que faz a sessão STOMP subir
 * em ~4,3 s (feita em paralelo, no `StompService`) ataca a causa; este módulo garante que, se ela
 * regredir ou se a máquina do usuário for lenta, o sintoma volte a ser lentidão — e não uma
 * afirmação falsa que bloqueia a tela.
 *
 * ⚠️ O que este módulo deliberadamente NÃO faz:
 * - **Não** encosta no prazo do alerta do AGENTE / cliente nativo. Aquele alerta tem outro dono
 *   (`timeUntilErrorMessageAppears`, no componente), outra causa e outro orçamento; misturar os dois
 *   num número só foi o que fez o prazo do STOMP nascer curto.
 * - **Não** silencia o alerta. Passado o prazo, o desfecho continua sendo o diálogo bloqueante com
 *   "Tentar novamente" — quando o servidor está mesmo fora, recarregar é o que resolve.
 * - **Não** guarda temporizador nem estado global: as funções são puras e recebem o tempo já
 *   decorrido. Quem mede é o consumidor, que é quem observa a mudança de status.
 */

/**
 * Pior orçamento de recuperação MEDIDO numa carga limpa (31/07/2026, DEV): worker mudo → 10 s de
 * watchdog → ~6,7 s do worker seguinte, somados ao que a SPA gasta antes da primeira tentativa.
 *
 * Existe como constante — e não como número solto num comentário — para virar piso executável em
 * `stompAlertDeadlineMs()`: nenhum prazo pode cair abaixo dele sem que o defeito volte.
 */
export const MEASURED_STOMP_RECOVERY_BUDGET_MS = 26000;

/**
 * Prazo até o portal acusar queda de servidor numa carga que **ainda não teve** STOMP.
 *
 * A conta: pior caso medido 26 s + ~10 s de um ciclo extra do watchdog + folga ≈ **40 s**, ou seja
 * ~1,5× o pior caso medido e ~9× o caminho já corrigido (~4,3 s). É o número que faz a carga limpa
 * deixar de abrir o modal, que era o defeito.
 *
 * O que 40 s NÃO cobre, de propósito: dois workers mudos seguidos (26 s + ~17 s ≈ 43 s). Nesse caso
 * o modal aparece — e aparecer está certo, porque o botão dele recarrega a página, que é exatamente
 * o que destrava dois workers mudos em sequência. Prazo maior atrasaria o aviso de um servidor
 * realmente fora sem ganhar nada.
 */
export const SERVER_UNREACHABLE_ALERT_DELAY_MS = 40000;

/**
 * Prazo até acusar queda de servidor quando o STOMP **já esteve de pé nesta carga da página**.
 *
 * Mais curto que o da carga fria porque a reconexão não paga as duas parcelas iniciais: o
 * `onStompConnect()` zera `connectionDelay`/`connectionTry` (ver `stomp.service.ts`), então a
 * primeira retentativa sai na hora, sem backoff acumulado, e o boot da SPA já aconteceu. Sobra o
 * mesmo caminho de worker (~10 s de watchdog + ~6,7 s do worker seguinte ≈ 17 s de pior caso
 * conhecido), com folga de ~1,7×.
 *
 * Continua muito acima dos 15 s de antes: quem estava trabalhando e perdeu o servidor é avisado
 * mais cedo que numa carga fria, mas nunca antes de a reconexão automática ter tido chance real.
 */
export const SERVER_UNREACHABLE_ALERT_DELAY_AFTER_DROP_MS = 30000;

/** Desfechos possíveis do alerta de STOMP. */
export type StompAlertOutcome =
    /** STOMP de pé: nada a mostrar. */
    'no-alert'
    /** Sem STOMP, mas ainda dentro do prazo — **não é erro** e a tela não pode ser bloqueada. */
    | 'still-connecting'
    /** Sem STOMP além do prazo: aí sim o portal afirma que o servidor está inalcançável. */
    | 'server-unreachable';

/** Texto em português de um desfecho. O componente não decide texto — só renderiza o que vem daqui. */
export interface StompAlertText {
    message: string;
    confirmButton: string;
}

/**
 * Prazo aplicável, em ms, dado se o STOMP já conectou alguma vez nesta carga da página.
 *
 * O `Math.max` com o orçamento medido é piso estrutural, não paranoia: o defeito do ticket foi
 * exatamente um prazo (15 s) abaixo do orçamento de recuperação (21-26 s). Quem for ajustar as
 * constantes acima não consegue reintroduzi-lo sem passar por aqui.
 */
export function stompAlertDeadlineMs(connectedAtLeastOnceInThisPageLoad: boolean): number {
    const configured = connectedAtLeastOnceInThisPageLoad
        ? SERVER_UNREACHABLE_ALERT_DELAY_AFTER_DROP_MS
        : SERVER_UNREACHABLE_ALERT_DELAY_MS;
    return Math.max(configured, MEASURED_STOMP_RECOVERY_BUDGET_MS);
}

/**
 * Decide o desfecho do alerta.
 *
 * @param stompConnected                    STOMP de pé agora?
 * @param connectedAtLeastOnceInThisPageLoad  já houve alguma conexão STOMP bem-sucedida nesta carga?
 * @param millisecondsWithoutStomp          há quantos ms o portal está sem STOMP.
 * @param deadlineMs                        prazo a aplicar; por padrão o de `stompAlertDeadlineMs`.
 *
 * Limites tratados de propósito:
 * - **exatamente no prazo** conta como estourado (`>=`): o orçamento medido cabe inteiro dentro do
 *   prazo com folga, então a igualdade não é caso de dúvida — e um `>` deixaria o desfecho depender
 *   de o relógio cair no milissegundo exato.
 * - **decorrido negativo, zero ou não finito** vira zero: relógio que ainda não começou, ou que
 *   andou para trás (`Date.now()` não é monotônico), não pode virar acusação de servidor fora. O
 *   erro seguro aqui é NÃO bloquear a tela.
 */
export function decideStompAlert(
    stompConnected: boolean,
    connectedAtLeastOnceInThisPageLoad: boolean,
    millisecondsWithoutStomp: number,
    deadlineMs: number = stompAlertDeadlineMs(connectedAtLeastOnceInThisPageLoad)
): StompAlertOutcome {
    if (stompConnected) {
        return 'no-alert';
    }
    const elapsedMs = Number.isFinite(millisecondsWithoutStomp) && millisecondsWithoutStomp > 0 ? millisecondsWithoutStomp : 0;
    return elapsedMs >= deadlineMs ? 'server-unreachable' : 'still-connecting';
}

/**
 * Texto de cada desfecho.
 *
 * `no-alert` devolve texto vazio de propósito: é o desfecho em que o consumidor não renderiza nada,
 * e devolver `null` só obrigaria o componente a um `if` a mais para o mesmo efeito.
 */
export function stompAlertText(outcome: StompAlertOutcome): StompAlertText {
    switch (outcome) {
        case 'server-unreachable':
            return {message: 'Não é possível conectar ao servidor.', confirmButton: 'Tentar novamente'};
        case 'still-connecting':
            return {message: 'Conectando ao servidor...', confirmButton: 'Aguardar'};
        case 'no-alert':
            return {message: '', confirmButton: ''};
    }
}
